/**
 * Responsive app frame — "Frost" (D88):
 *  - phone (< 640px): top bar + floating bottom tab bar (Home, Swap, Activity, Vault, Settings)
 *  - tablet (640–1023px): top bar with round icon buttons
 *  - desktop (≥ 1024px): frosted left sidebar with the profile at the bottom
 */
import type { SystemMessage } from "@custos/api-client";
import { Logo, NetworkBadge, SystemMessages, cn } from "@custos/ui";
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  History,
  Home,
  LogOut,
  MessageCircle,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { api, queryClient } from "../lib/api";
import { qk, useChatUnread, useConfig, useMe, useMessages } from "../lib/queries";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  tab?: boolean;
}

const NAV: NavItem[] = [
  { to: "/", label: "Home", icon: Home, tab: true },
  { to: "/deposit", label: "Deposit", icon: ArrowDownLeft },
  { to: "/withdraw", label: "Withdraw", icon: ArrowUpRight },
  { to: "/swap", label: "Swap", icon: ArrowLeftRight, tab: true },
  { to: "/activity", label: "Activity", icon: History, tab: true },
  { to: "/vault", label: "Vault", icon: ShieldCheck, tab: true },
  { to: "/settings", label: "Settings", icon: Settings, tab: true },
];

/** Desktop sidebar; Deposit and Withdraw are the quick actions on Home there. */
const SIDE: NavItem[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/swap", label: "Swap", icon: ArrowLeftRight },
  { to: "/activity", label: "Activity", icon: History },
  { to: "/vault", label: "Vault", icon: ShieldCheck },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/help", label: "Help", icon: MessageCircle },
];

export function useLogout() {
  const navigate = useNavigate();
  return async () => {
    await api.logout();
    queryClient.clear();
    navigate("/login", { replace: true });
  };
}

function Badge() {
  const config = useConfig();
  return config.data ? <NetworkBadge label={config.data.badge} /> : null;
}

/** Messages from the team, above every page (D78). Closing one is remembered on all devices. */
function TeamMessages() {
  const messages = useMessages();
  const dismiss = (id: string) => {
    queryClient.setQueryData<SystemMessage[]>(qk.messages, (list) => list?.filter((m) => m.id !== id));
    api.dismissMessage(id).catch(() => void queryClient.invalidateQueries({ queryKey: qk.messages }));
  };
  return <SystemMessages className="mb-4" messages={messages.data ?? []} onDismiss={dismiss} />;
}

/** Round chat button (D79): in the top bar on phones, bottom-right otherwise; the count = unread replies. */
/** Phone width, live (the chat button sits in the top bar there instead of floating over content, D80). */
function useIsPhone(): boolean {
  const query = "(max-width: 639px)";
  const [phone, setPhone] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return;
    const onChange = () => setPhone(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return phone;
}

function ChatButton({ inBar = false }: { inBar?: boolean }) {
  const unread = useChatUnread();
  const location = useLocation();
  if (location.pathname === "/help") return null;
  const n = unread.data ?? 0;
  return (
    <NavLink
      to="/help"
      aria-label={n > 0 ? `Help chat, ${n} unread` : "Help chat"}
      title="Help chat"
      data-testid="chat-button"
      className={
        inBar
          ? "glass-circle relative inline-flex size-11 items-center justify-center rounded-full"
          : "bg-primary-gradient fixed right-6 bottom-6 z-30 inline-flex size-14 items-center justify-center rounded-full shadow-pop transition-transform hover:scale-105"
      }
    >
      <MessageCircle className={inBar ? "size-5" : "size-6"} aria-hidden />
      {n > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-white"
          data-testid="chat-unread"
        >
          {n}
        </span>
      )}
    </NavLink>
  );
}

/** Desktop: frosted sidebar, as tall as the window. */
function Sidebar({ onLogout }: { onLogout: () => void }) {
  const me = useMe();
  const unread = useChatUnread().data ?? 0;
  const email = me.data?.email ?? "";
  return (
    <aside className="glass fixed inset-y-4 left-4 z-30 hidden w-56 flex-col rounded-[28px] p-4 lg:flex">
      <Logo className="px-2 pt-1" />
      <nav aria-label="Main navigation" data-testid="sidenav" className="mt-6 flex flex-col gap-1">
        {SIDE.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              cn(
                "flex min-h-11 items-center gap-3 rounded-full px-3.5 text-sm font-medium transition-colors duration-200",
                isActive
                  ? "bg-surface font-semibold text-primary-strong shadow-sm"
                  : "text-muted hover:bg-hover hover:text-fg",
              )
            }
          >
            <item.icon className="size-5 shrink-0" aria-hidden />
            {item.label}
            {item.to === "/help" && unread > 0 && (
              <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[11px] font-semibold text-white">
                {unread}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto flex flex-col gap-3">
        <div className="px-2">
          <Badge />
        </div>
        <div className="flex items-center gap-2.5 rounded-full bg-surface/60 p-1.5">
          <span
            aria-hidden
            className="bg-primary-gradient inline-flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold uppercase"
          >
            {email.slice(0, 1)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-fg" title={email}>
              {email}
            </p>
            {me.data?.totpEnabled && <p className="text-[11px] text-muted">2FA on</p>}
          </div>
          <button
            type="button"
            onClick={onLogout}
            title="Log out"
            aria-label="Log out"
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-muted hover:bg-hover hover:text-fg"
          >
            <LogOut className="size-4" aria-hidden />
          </button>
        </div>
      </div>
    </aside>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const logout = useLogout();
  const location = useLocation();
  const phone = useIsPhone();

  return (
    <div className="flex min-h-dvh flex-col lg:pl-64">
      <Sidebar onLogout={logout} />

      {/* Tablet: top bar with round icon navigation */}
      <header className="sticky top-0 z-30 hidden backdrop-blur-xl sm:block lg:hidden">
        <div className="mx-auto flex items-center gap-3 px-6 py-4">
          <Logo compact />
          <nav
            aria-label="Main navigation"
            data-testid="topnav"
            className="mx-auto flex items-center gap-1.5"
          >
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                title={item.label}
                aria-label={item.label}
                className={({ isActive }) =>
                  cn(
                    "inline-flex size-11 items-center justify-center rounded-full transition-colors duration-200",
                    isActive ? "animate-pop bg-primary-gradient" : "glass-circle",
                  )
                }
              >
                <item.icon className="size-5" aria-hidden />
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Badge />
            <button
              type="button"
              onClick={logout}
              title="Log out"
              aria-label="Log out"
              className="glass-circle inline-flex size-11 items-center justify-center rounded-full"
            >
              <LogOut className="size-5" aria-hidden />
            </button>
          </div>
        </div>
      </header>

      {/* Phone top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between px-4 py-3 backdrop-blur-xl sm:hidden">
        <Logo />
        <div className="flex items-center gap-2">
          <Badge />
          {phone && <ChatButton inBar />}
        </div>
      </header>

      <main
        key={location.pathname}
        className="mx-auto w-full max-w-7xl flex-1 animate-fade px-4 pt-3 pb-32 sm:px-6 sm:pt-4 sm:pb-12 lg:pt-6 lg:pr-8 lg:pl-4"
      >
        <TeamMessages />
        {children}
      </main>

      {!phone && <ChatButton />}

      {/* Phone floating bottom tab bar */}
      <nav
        aria-label="Tabs"
        data-testid="tabbar"
        className="glass fixed inset-x-3 bottom-[calc(0.75rem+env(safe-area-inset-bottom))] z-30 grid grid-cols-5 gap-1 rounded-full p-1.5 sm:hidden"
      >
        {NAV.filter((i) => i.tab).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              cn(
                "flex min-h-13 flex-col items-center justify-center gap-0.5 rounded-full text-[10px] font-medium transition-colors duration-200",
                isActive ? "animate-pop bg-primary-gradient" : "text-muted",
              )
            }
          >
            <item.icon className="size-5" aria-hidden />
            {item.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3 sm:mb-6">
      <div className="min-w-0">
        <h1 className="font-heading text-[28px] leading-tight font-semibold tracking-[-0.03em] text-fg sm:text-4xl">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}
